import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Store, select } from '@ngrx/store'
import {
  Channel,
  Rental,
  ChannelNameTokens,
  untilDestroy,
  Destroyable,
  selectOnce,
  loadRentals,
  selectRentalById,
  isSomething,
  RatesChangedComplete,
  GroupRatesChangedComplete,
  selectSomeOnce,
  ActionFailed,
  ActionSkipped,
} from '@tokeet-frontend/tv3-platform'
import {
  switchMap,
  take,
  tap,
  finalize,
  filter,
  debounceTime,
  map,
  startWith,
  concatMap,
  takeUntil,
} from 'rxjs/operators'
import {
  selectAvailableRentalForNewAPIConnection,
  selectConnectionById,
} from '@tv3/store/connection/connection.selectors'
import { merge, Observable, of } from 'rxjs'
import * as moment from 'moment'
import { v4 as uuid } from 'uuid'
import {
  Connection,
  getRealChannelId,
  isRecentlyLinked,
  stampedSinceLink,
} from '@tv3/store/connection/connection.model'
import { getConnectionStatusReasonText, isReconnectableReason } from '@tv3/store/connection/connection.view'
import { ChannelConnectWizardsService } from '@tv3/containers/channels/channel-connect/channel-connect-wizards/index.service'
import {
  PushAvailabilityToConnectionForWizard,
  PushAvailabilityToConnectionForWizardComplete,
  HoldEventForEmptyCalendarPush,
  ImportBookingsFromConnection,
  ImportBookingsFromConnectionComplete,
  LinkConnectionWithRental,
  LoadConnectionsByChannel,
} from '@tv3/store/connection/connection.actions'
import { Actions, ofType } from '@ngrx/effects'
import { ConnectionHelperService } from '../../../connection-helper.service'
import { FormControl, Validators } from '@angular/forms'
import { AirbnbHelperService } from '@tv3/containers/channels/channel-connect/airbnb-helper.service'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '@tv3/containers/rentals/overlays/rental-overlay/rental-overlay.component'

@Component({
  selector: 'app-airbnb-listing-wizard-step1',
  templateUrl: './airbnb-listing-wizard-step1.component.html',
  styleUrls: ['./airbnb-listing-wizard-step1.component.scss'],
})
export class AirbnbListingWizardStep1Component extends Destroyable implements OnInit {
  @Output() next = new EventEmitter()
  @Output() coHost = new EventEmitter()

  @Input() connection: Connection
  @Input() channel: Channel

  rentalIdCtrl = new FormControl('', [Validators.required])

  rentals$: Observable<Rental[]>

  private tmpHoldEventId: string

  isProcessing = false
  isLinked = false
  isBookingImported = false
  isAvailabilityPushed = false
  isRentalRatesReady = false
  ratesMissing: string[] = []

  // Auto-chain: after Link the remaining steps of this section (import bookings, push
  // availability) run on their own, and the wizard resumes them when reopened mid-way.
  autoStep: 'import' | 'availability' | null = null
  autoChainFailed = false
  private autoChainStarted = false

  get isStepComplete(): boolean {
    return this.isLinked && this.isBookingImported && this.isAvailabilityPushed
  }

  // A disabled connection cannot reach Airbnb, so every action on this step would fail.
  // Derived, not cached: the parent re-binds `connection` from the store once a
  // reconnect lands, and the banner has to clear itself when it does.
  get isDisconnected(): boolean {
    // tslint:disable-next-line:triple-equals
    return this.connection?.status == 0
  }

  get disconnectedMessage(): string {
    return this.isDisconnected ? getConnectionStatusReasonText(this.connection?.statusReason) : ''
  }

  // A suspended host is not something re-running OAuth can fix.
  get canReconnect(): boolean {
    return this.isDisconnected && isReconnectableReason(this.connection?.statusReason)
  }

  constructor(
    private store: Store<any>,
    private actions: Actions,
    private connectionHelperService: ConnectionHelperService,
    private airbnbHelperService: AirbnbHelperService,
    private channelConnectWizards: ChannelConnectWizardsService
  ) {
    super()
  }

  ngOnInit(): void {
    this.rentals$ = this.store.pipe(
      selectOnce(selectAvailableRentalForNewAPIConnection, {
        id: this.channel.id,
        selected: this.connection.rentalId,
      })
    )

    this.rentalIdCtrl.setValue(this.connection.rentalId)
    if (this.connection.rentalId) {
      this.rentalIdCtrl.disable()
      this.isLinked = true
    }
    this.syncCompletedStepsFromConnection()

    // save temporary hold event id
    this.actions
      .pipe(
        ofType(HoldEventForEmptyCalendarPush),
        tap(({ holdEvent }) => (this.tmpHoldEventId = holdEvent.id)),
        untilDestroy(this)
      )
      .subscribe()

    const rentalIdChanges$ = this.rentalIdCtrl.valueChanges.pipe(startWith(this.rentalIdCtrl.value))
    merge(this.actions.pipe(ofType(RatesChangedComplete, GroupRatesChangedComplete)), rentalIdChanges$)
      .pipe(
        debounceTime(500),
        map(() => this.rentalIdCtrl.value),
        filter(isSomething),
        switchMap((rentalId) => this.store.pipe(selectSomeOnce(selectRentalById(rentalId)))),
        filter(isSomething),
        // Silent readiness check: rates being incomplete no longer blocks the wizard,
        // the user reviews and confirms rates on the Rates step instead.
        switchMap((rental) => this.connectionHelperService.getRentalRatesReadiness(rental, this.connection)),
        untilDestroy(this)
      )
      .subscribe(({ ready, missing }) => {
        this.isRentalRatesReady = ready
        this.ratesMissing = missing
      })

    // Resume: a linked connection with pending steps continues the automated chain
    // right away, so reopening the wizard acts as a progress view, not a restart.
    this.startAutoChain()
  }

  // Seeds the per-step completion flags from the durable timestamps on the connection —
  // the same fields isApiConnectionActive() reads. This is what makes the wizard
  // resumable: the flags survive closing the dialog because the store has them.
  // Only stamps written after the current link count (TV4-5891): the room stamps survive
  // an unlink/re-link and would otherwise skip the import and show stale green ticks.
  // An unlinked room has no linkDate to compare against (and nothing done for the
  // upcoming link), so its stamps must not seed anything: the flags only ever go up,
  // and a pre-link seed would survive the link and skip the auto-chain.
  private syncCompletedStepsFromConnection() {
    this.isLinked = this.isLinked || !!this.connection?.rentalId
    if (!this.connection?.rentalId) {
      return
    }
    this.isBookingImported = this.isBookingImported || stampedSinceLink(this.connection, this.connection?.lastavailpull)
    this.isAvailabilityPushed =
      this.isAvailabilityPushed || stampedSinceLink(this.connection, this.connection?.lastavailpush)
  }

  onRetryAutoChain() {
    this.autoChainStarted = false
    this.startAutoChain()
  }

  // Runs the remaining automated steps of this section in order: import bookings, then
  // push availability. Both are safe to run unattended (import is additive, availability
  // reflects the AdvanceCM calendar, and the backend schedules a push at link time too).
  // Rates are NOT pushed from here — that decision lives on the Rates step.
  private startAutoChain() {
    if (
      this.autoChainStarted ||
      this.isDisconnected ||
      !this.isLinked ||
      this.isStepComplete ||
      !isRecentlyLinked(this.connection)
    ) {
      return
    }
    this.autoChainStarted = true
    this.autoChainFailed = false
    this.isProcessing = true

    this.runAutoImportBookings()
      .pipe(
        concatMap((ok) => (ok ? this.runAutoPushAvailability() : of(false))),
        finalize(() => {
          this.isProcessing = false
          this.autoStep = null
        }),
        untilDestroy(this)
      )
      .subscribe(
        (ok) => {
          if (ok && this.isStepComplete) {
            this.next.emit()
          } else {
            this.autoChainFailed = true
          }
        },
        () => (this.autoChainFailed = true)
      )
  }

  private runAutoImportBookings(): Observable<boolean> {
    if (this.isBookingImported) {
      return of(true)
    }
    this.autoStep = 'import'
    const requestId = uuid()
    // Re-select the connection: right after linking, the @Input may not carry the
    // freshly-assigned rentalId yet, and the import payload needs it.
    return this.store.pipe(
      select(selectConnectionById, { id: this.connection.id }),
      take(1),
      switchMap((connection) => {
        this.store.dispatch(
          ImportBookingsFromConnection({
            connId: connection.id,
            channelId: connection.channelId,
            channelName: ChannelNameTokens.AirBnBV2API,
            data: connection,
            requestId,
          })
        )
        return this.actions.pipe(
          ofType(ImportBookingsFromConnectionComplete, ActionSkipped, ActionFailed),
          // Complete actions carry the request id: only this chain's own import counts.
          filter(
            (action) => action.type !== ImportBookingsFromConnectionComplete.type || action.requestId === requestId
          ),
          take(1)
        )
      }),
      map((action) => {
        // ActionSkipped is the effect's "Airbnb import is still processing" outcome — the
        // import is scheduled server-side, so the chain can move on. The effect does not
        // record lastavailpull in that case, so persist it here: the stepper's Connecting
        // [completed] binding and wizard resume both read it.
        if (action.type === ActionSkipped.type) {
          this.store.dispatch(
            ImportBookingsFromConnectionComplete({
              update: { id: this.connection.id, changes: { lastavailpull: moment.utc().unix() } },
              requestId,
            })
          )
        }
        const ok = action.type !== ActionFailed.type
        this.isBookingImported = this.isBookingImported || ok
        return ok
      })
    )
  }

  private runAutoPushAvailability(): Observable<boolean> {
    if (this.isAvailabilityPushed) {
      return of(true)
    }
    this.autoStep = 'availability'
    return this.store.pipe(
      select(selectConnectionById, { id: this.connection.id }),
      take(1),
      switchMap((connection) => {
        this.store.dispatch(
          PushAvailabilityToConnectionForWizard({
            connId: connection.id,
            channelId: connection.channelId,
            channelName: ChannelNameTokens.AirBnBV2API,
            data: connection,
            tmpHoldEventId: this.tmpHoldEventId,
          })
        )
        // HoldEventForEmptyCalendarPush is the empty-calendar (424) fallback outcome:
        // a temporary hold event was just created. Treated as not-ok so the Retry banner
        // shows; the retry re-pushes with the hold id the ngOnInit listener captured.
        return this.actions.pipe(
          ofType(
            PushAvailabilityToConnectionForWizardComplete,
            HoldEventForEmptyCalendarPush,
            ActionSkipped,
            ActionFailed
          ),
          take(1)
        )
      }),
      map((action) => {
        const ok = action.type === PushAvailabilityToConnectionForWizardComplete.type
        this.isAvailabilityPushed = this.isAvailabilityPushed || ok
        return ok
      })
    )
  }

  onReconnect() {
    this.channelConnectWizards.connect(this.channel).subscribe(() => this.onRefreshRentals())
  }

  onRefreshRentals() {
    this.store.dispatch(loadRentals({ less: true }))
    this.store.dispatch(
      LoadConnectionsByChannel({
        channelName: ChannelNameTokens.AirBnBV2API,
        channelId: getRealChannelId(this.connection.channelId),
      })
    )
  }

  onImportListing() {
    this.airbnbHelperService.importRoom(ChannelNameTokens.AirBnBV2API, this.connection).subscribe()
  }

  onChangeSyncType() {
    this.airbnbHelperService.setSyncCategoryV2(this.connection).subscribe()
  }

  onImport() {
    this.isProcessing = true
    this.connectionHelperService
      .importBookings(ChannelNameTokens.AirBnBV2API, this.connection)
      .pipe(
        take(1),
        tap(() => (this.isBookingImported = true)),
        finalize(() => (this.isProcessing = false))
      )
      .subscribe()
  }

  onPushAvailability() {
    this.isProcessing = true
    this.connectionHelperService
      .pushAvailabilityForWizard(ChannelNameTokens.AirBnBV2API, this.connection, this.tmpHoldEventId)
      .pipe(
        switchMap(() => this.actions.pipe(ofType(PushAvailabilityToConnectionForWizardComplete))),
        take(1),
        tap(() => (this.isAvailabilityPushed = true)),
        finalize(() => (this.isProcessing = false))
      )
      .subscribe()
  }

  onRates() {
    this.store.dispatch(OpenRentalOverlay({ rentalId: this.rentalIdCtrl.value, activeTab: RentalDialogTab.RatesTable }))
  }

  onLink() {
    this.isProcessing = true
    this.store.dispatch(
      LinkConnectionWithRental({
        connId: this.connection.id,
        channelName: ChannelNameTokens.AirBnBV2API,
        data: {
          rentalId: this.rentalIdCtrl.value,
          channelId: this.connection.channelId,
          propertyId: this.connection.propertyId,
          roomId: this.connection.roomId,
        },
      })
    )
    // Success is detected on the store, not the action stream: ActionFailed is
    // application-wide, so an unrelated failure must not be mistaken for this link's.
    // rentalId lands on this connection only through LinkConnectionWithRentalComplete.
    const linked$ = this.store.pipe(
      select(selectConnectionById, { id: this.connection.id }),
      filter((connection) => !!connection?.rentalId),
      take(1)
    )

    // A failure anywhere just releases the spinner; the link watcher below stays armed
    // and still picks the link up if it completes later. Disarmed once linked so a later
    // unrelated failure cannot wipe the auto-chain's own spinner.
    this.actions
      .pipe(ofType(ActionFailed), take(1), takeUntil(linked$), untilDestroy(this))
      .subscribe(() => (this.isProcessing = false))

    linked$.pipe(untilDestroy(this)).subscribe((connection) => {
      // This runs on the store emission, before change detection rebinds the
      // [connection] input — take the fresh connection here, or the chain's
      // recently-linked gate would still read the pre-link one without a linkDate.
      this.connection = connection
      // isProcessing must be released before starting the chain: the chain manages
      // the flag itself and a trailing reset here would wipe its spinner.
      this.isProcessing = false
      this.isLinked = true
      this.startAutoChain()
    })
  }
}
